import React from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'lib/components/Tooltip';
import { OrganizationStatus } from 'lib/enums';
import { isColumnUser } from 'lib/helpers';
import { EOrganization, ESnapshot, exists } from 'lib/types';
import {
  selectActiveOrganization,
  selectIsImpersonating,
  selectUser
} from 'redux/auth';
import { useAppSelector } from 'redux/hooks';
import { userIsSuper } from 'utils/permissions';
import { Badge, BadgeProps } from 'lib/components/Badge/Badge';

const getOrganizationStatusAlertProps = (
  activeOrganization?: ESnapshot<EOrganization> | null
): {
  alertProps: { title: string; status: BadgeProps['status'] };
  tooltip: string;
} | null => {
  if (!exists(activeOrganization)) {
    return null;
  }

  const organizationStatusEnum = OrganizationStatus.by_value(
    activeOrganization.data().organizationStatus
  );

  if (!organizationStatusEnum) {
    return null;
  }

  const title = organizationStatusEnum.label;
  const tooltip = organizationStatusEnum.description;

  switch (organizationStatusEnum.value) {
    case OrganizationStatus.in_implementation.value: {
      return { tooltip, alertProps: { title, status: 'info' } };
    }

    case OrganizationStatus.state_site_uploader.value: {
      return { tooltip, alertProps: { title, status: 'warning' } };
    }

    case OrganizationStatus.express.value: {
      return { tooltip, alertProps: { title, status: 'warning' } };
    }

    case OrganizationStatus.disengaged.value: {
      return { tooltip, alertProps: { title, status: 'critical' } };
    }

    case OrganizationStatus.placement_only.value: {
      return { tooltip, alertProps: { title, status: 'critical' } };
    }

    case OrganizationStatus.live.value:
    case OrganizationStatus.not_applicable.value:
    case undefined:
    default: {
      return null;
    }
  }
};

/**
 * Ex: johndoe@foobar.com -> j***e@foobar.com
 */
const anonymizeEmail = (email: string): string => {
  const [user, domain] = email.split('@');
  if (!user || !domain) {
    return '';
  }

  const anonymizedUser = `${user.charAt(0)}***${user.charAt(user.length - 1)}`;
  return `${anonymizedUser}@${domain}`;
};

export function InternalLabels() {
  const user = useAppSelector(selectUser);

  const activeOrganization = useAppSelector(selectActiveOrganization);
  const isImpersonating = useAppSelector(selectIsImpersonating);

  const organizationStatusAlertProps =
    getOrganizationStatusAlertProps(activeOrganization);

  if (!exists(user)) {
    return <></>;
  }

  const anonymizedEmail = anonymizeEmail(user.data().email ?? '');

  return (
    <div className="ml-10 flex gap-3 items-center">
      {isColumnUser(user) && userIsSuper(user) && (
        <Badge
          size="lg"
          status="info"
          startIcon={<UserIcon className="w-4 h-4" />}
        >
          Super User
        </Badge>
      )}

      {isImpersonating && (
        <Badge
          size="lg"
          status="critical"
          startIcon={<UserIcon className="w-5 h-5" />}
        >
          Impersonating {anonymizedEmail}
        </Badge>
      )}

      {isColumnUser(user) && organizationStatusAlertProps && (
        <Tooltip
          helpText={organizationStatusAlertProps.tooltip}
          position="left"
        >
          <Badge
            status={organizationStatusAlertProps.alertProps.status}
            size="lg"
          >
            {organizationStatusAlertProps.alertProps.title}
          </Badge>
        </Tooltip>
      )}
    </div>
  );
}
